import { useCallback, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Box, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { TProjectFormData } from 'src/@types';
import FormListActionButtons from 'src/components/FormListActionButtons';
import { RHFDateWithCheckbox, RHFTextField } from 'src/components/hook-form';
import ItemActionMenu from 'src/components/ItemActionMenu';
import { useExcelFormField } from 'src/hooks/useExcelFormField';
import { numberToSimpleKoreanCurrency } from 'src/utils/koreanCurrency';
import {
  downloadProjectRoundSalesTemplate,
  parseProjectRoundSalesExcel,
  ProjectRoundSaleRow,
} from 'src/utils/projectExcelUtils';

import BulkRegisterDialog from './BulkRegisterDialog';

export interface ProjectRoundSalesSectionProps {
  methods: UseFormReturn<TProjectFormData>;
  readonly?: boolean;
}

type FormRow = NonNullable<TProjectFormData['projectRoundSales']>[number];

/**
 * 기성내역 섹션 컴포넌트
 * - 세금계산서 발행 예정일/발행 여부
 * - 수금 예정일/수금 여부
 */
function ProjectRoundSalesSection({ methods, readonly }: ProjectRoundSalesSectionProps) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    fields: projectRoundSales,
    append: appendProjectRoundSale,
    insert: insertProjectRoundSale,
    remove: removeProjectRoundSale,
    fileInputRef,
    handleDownload,
    handleUpload,
    handleClickUpload,
  } = useExcelFormField<TProjectFormData, 'projectRoundSales', ProjectRoundSaleRow, FormRow>({
    methods,
    fieldName: 'projectRoundSales',
    downloadFn: downloadProjectRoundSalesTemplate,
    parseFn: parseProjectRoundSalesExcel,
    formToExcel: (item) => ({
      amt: item.amt || 0,
      issueDay: item.issueDay || '',
      issueYn: item.issueYn || false,
      paymentDay: item.paymentDay || '',
      paymentYn: item.paymentYn || false,
      memo: item.memo || null,
    }),
    excelToForm: (row) => ({
      amt: row.amt,
      issueDay: row.issueDay || Date.now(), // 이미 timestamp
      issueYn: row.issueYn,
      paymentDay: row.paymentDay || Date.now(), // 이미 timestamp
      paymentYn: row.paymentYn,
      memo: row.memo || '',
    }),
    labels: {
      download: '기성내역',
      upload: '건의 기성내역이 업로드되었습니다.',
      empty: '업로드할 데이터가 없습니다.',
    },
  });

  // 기성내역 복사 함수
  const handleCopyItem = useCallback(
    (index: number) => {
      const currentValues = methods.getValues('projectRoundSales');
      const itemToCopy = currentValues?.[index];

      if (itemToCopy) {
        const copiedItem = {
          amt: itemToCopy.amt || 0,
          issueDay: itemToCopy.issueDay || Date.now(),
          issueYn: false,
          paymentDay: itemToCopy.paymentDay || Date.now(),
          paymentYn: false,
          memo: itemToCopy.memo || '',
        };

        insertProjectRoundSale(index + 1, copiedItem);
        enqueueSnackbar('기성내역이 복사되었습니다.', { variant: 'success' });
      }
    },
    [insertProjectRoundSale, methods, enqueueSnackbar],
  );

  const handleAddItem = useCallback(() => {
    appendProjectRoundSale({
      amt: 0,
      issueDay: Date.now(),
      issueYn: false,
      paymentDay: Date.now(),
      paymentYn: false,
      memo: '',
    });
  }, [appendProjectRoundSale]);

  // 일괄 등록 다이얼로그
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAmt, setBulkAmt] = useState<number>(0);
  const [bulkMemo, setBulkMemo] = useState<string>('');

  // 비고 템플릿 토큰 치환 (기준: 세금계산서 발행 예정일)
  const formatMemo = useCallback((template: string, ts: number) => {
    if (!template) return '';
    const d = dayjs(ts);
    return template
      .replace(/\{YYYY\}/g, d.format('YYYY'))
      .replace(/\{YY\}/g, d.format('YY'))
      .replace(/\{MM\}/g, d.format('MM'))
      .replace(/\{M\}/g, d.format('M'))
      .replace(/\{DD\}/g, d.format('DD'));
  }, []);

  const handleBulkConfirm = useCallback(
    (rows: { issueDay: number; paymentDay: number }[]) => {
      rows.forEach((row) => {
        appendProjectRoundSale({
          amt: bulkAmt,
          issueDay: row.issueDay,
          issueYn: false,
          paymentDay: row.paymentDay,
          paymentYn: false,
          memo: formatMemo(bulkMemo, row.issueDay),
        });
      });
      enqueueSnackbar(`${rows.length}건의 기성내역이 추가되었습니다.`, { variant: 'success' });
      setBulkAmt(0);
      setBulkMemo('');
    },
    [appendProjectRoundSale, bulkAmt, bulkMemo, enqueueSnackbar, formatMemo],
  );

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {!readonly && (
        <FormListActionButtons
          fileInputRef={fileInputRef}
          onDownload={handleDownload}
          onClickUpload={handleClickUpload}
          onUpload={handleUpload}
          onAdd={handleAddItem}
          addLabel="기성금액 추가"
          onBulkRegister={() => setBulkOpen(true)}
          bulkRegisterLabel="일괄 등록"
        />
      )}

      <BulkRegisterDialog
        open={bulkOpen}
        title="기성내역 일괄 등록"
        dateFields={[
          { key: 'issueDay', label: '세금계산서 발행 예정일', defaultDay: 'LAST' },
          { key: 'paymentDay', label: '수금 예정일', defaultDay: 'LAST' },
        ]}
        onClose={() => setBulkOpen(false)}
        onConfirm={(rows) =>
          handleBulkConfirm(rows.map((r) => ({ issueDay: r.issueDay, paymentDay: r.paymentDay })))
        }
      >
        {({ previewRows }) => {
          const firstTs = previewRows[0]?.issueDay;
          const memoPreview =
            bulkMemo && firstTs !== undefined ? formatMemo(bulkMemo, firstTs) : '';
          return (
            <Stack spacing={2}>
              <TextField
                label="금액"
                size="small"
                value={bulkAmt ? bulkAmt.toLocaleString() : ''}
                onChange={(e) => {
                  const v = Number(e.target.value.replace(/,/g, ''));
                  if (!isNaN(v)) setBulkAmt(v);
                }}
                InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                helperText={bulkAmt ? numberToSimpleKoreanCurrency(bulkAmt) : ''}
              />
              <TextField
                label="비고"
                size="small"
                multiline
                minRows={2}
                value={bulkMemo}
                onChange={(e) => setBulkMemo(e.target.value)}
                helperText={
                  <>
                    토큰: {'{YYYY}'} {'{YY}'} {'{MM}'} {'{M}'} {'{DD}'} (기준: 세금계산서 발행
                    예정일)
                    {memoPreview && (
                      <>
                        <br />
                        예시 (첫 건): {memoPreview}
                      </>
                    )}
                  </>
                }
              />
            </Stack>
          );
        }}
      </BulkRegisterDialog>
      {projectRoundSales.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            p: 3,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2">기성내역이 없습니다.</Typography>
        </Box>
      ) : (
        projectRoundSales.map((field, index) => (
          <Box
            key={field.id}
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{
              p: 2,
              pl: readonly ? 2 : 4,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              position: 'relative',
            }}
          >
            {!readonly && (
              <ItemActionMenu
                onDelete={() => removeProjectRoundSale(index)}
                onCopy={() => handleCopyItem(index)}
                deleteLabel="삭제"
                copyLabel="복사"
              />
            )}

            {/* 첫 번째 줄: 금액 + 비고 */}
            <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2} alignItems="flex-start">
              <Controller
                name={`projectRoundSales.${index}.amt`}
                control={methods.control}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="금액"
                    value={field.value ? field.value.toLocaleString() : ''}
                    placeholder="금액"
                    error={!!error}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numericValue = Number(value.replace(/,/g, ''));
                      if (!isNaN(numericValue)) {
                        field.onChange(numericValue);
                      }
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">원</InputAdornment>,
                      readOnly: readonly,
                    }}
                    helperText={
                      error
                        ? error?.message
                        : field.value
                        ? `${numberToSimpleKoreanCurrency(field.value)}`
                        : ''
                    }
                    inputRef={ref}
                    sx={{ flex: 1, minWidth: 180 }}
                  />
                )}
              />
              <RHFTextField
                name={`projectRoundSales.${index}.memo`}
                label="비고"
                multiline
                inputProps={{ readOnly: readonly }}
                sx={{ flex: 2, minWidth: 200 }}
              />
            </Box>

            <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
              <RHFDateWithCheckbox
                dateName={`projectRoundSales.${index}.issueDay`}
                checkboxName={`projectRoundSales.${index}.issueYn`}
                label="세금계산서 발행 예정일"
                checkedLabel="세금계산서 발행일"
                checkboxLabel="발행 여부"
                readOnly={readonly}
                sx={{ flex: 1, minWidth: 250 }}
              />
              <RHFDateWithCheckbox
                dateName={`projectRoundSales.${index}.paymentDay`}
                checkboxName={`projectRoundSales.${index}.paymentYn`}
                label="수금 예정일"
                checkedLabel="수금일"
                checkboxLabel="수금 여부"
                readOnly={readonly}
                sx={{ flex: 1, minWidth: 250 }}
              />
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}

export default ProjectRoundSalesSection;
